import { Document, FieldConfig } from '@lucidtech/las-sdk-browser';

import {
  amountFormatter,
  currencyFormatter,
  dateFormatter,
  DEFAULT_TEMPLATE,
  highConfidenceValidator,
} from '@/components/ui';
import { FORMATTER_CLASSIFICATION_FUNCTION_ID } from '@/constants';
import { useClient } from '@/hooks';
import { FieldState } from '@/store';
import { convertToFieldConfig } from '@/utils';

export const sample = {
  documentType: 'Invoices',
  fields: [
    {
      id: 'supplier_name',
      type: 'single-value',
      name: 'Supplier',
      promptHint: 'The company that issued the invoice.',
    },
    {
      id: 'invoice_id',
      type: 'single-value',
      name: 'Invoice Number',
      promptHint: 'The invoice number.',
    },
    {
      id: 'invoice_date',
      type: 'single-value',
      name: 'Invoice Date',
      formatterHint: 'date',
      promptHint: 'The invoice date.',
    },
    {
      id: 'total_amount',
      type: 'single-value',
      name: 'Total Amount',
      formatterHint: 'numeric',
      promptHint: 'The total amount, including taxes.',
    },
    {
      id: 'currency',
      type: 'single-value',
      name: 'Currency',
      formatterHint: 'currency',
      promptHint: 'The ISO currency of the total amount.',
    },
    {
      id: 'document_type',
      name: 'Document Type',
      enumeration: ['Invoice', 'Receipt', 'Credit Note', 'Other'],
      formatterHint: 'classification',
      promptHint: 'The ISO currency of the total amount.',
    },
    {
      id: 'invoice_lines',
      type: 'table',
      name: 'Invoice Line Items',
      promptHint: 'The invoice line items.',
      fields: [
        {
          id: 'description',
          type: 'single-value',
          name: 'Line Description',
          promptHint: 'A description of the purchased product or service',
        },
        {
          id: 'line_total',
          type: 'single-value',
          name: 'Line Total',
          formatterHint: 'numeric',
          promptHint: 'The line total amount',
        },
      ],
    },
  ],
};

const createDocumentPrompt = (description: string) => `
You are an AI model that suggests field configurations for a document processing task.
    
* Fields that always have a numerical value should have formatterHint set to "numeric".
* Fields that always have a valid date value should have formatterHint set to "date".
* Fields that can repeat, such as line items in an invoice or rows in a table, should have formatterHint set to "table"
* Classification fields should have formatterHint set to "classification" and an enumeration of possible values.
* Prompt hints or field names should be generic for the document type as a whole, not specific to a single document.

The field configuration should be returned on the same format as in the following example:

${JSON.stringify(sample)}

Create a field configuration based on the provided image and the following description:
${description}

Create at most 8 fields, including table fields. If the provided image is not a document, return an empty JSON dict.

Output _only_ the JSON data, _nothing_ else. 
`;

type FieldStateWithHints = Omit<FieldState, 'fields'> & {
  formatterHint?: string;
  enumeration?: string[];
  fields?: FieldStateWithHints[];
};

const applyFormattersAndValidators = (field: FieldStateWithHints): FieldState => {
  const result = {
    ...field,
    fields: [] as FieldState[],
    formatterHint: undefined,
    enumeration: undefined,
  };

  if (field.type === 'table') {
    result.fields = field.fields?.map(applyFormattersAndValidators) ?? [];
  } else {
    if (field.formatterHint === 'date') {
      result.formatters = [dateFormatter];
    } else if (field.formatterHint === 'numeric') {
      result.formatters = [amountFormatter];
    } else if (field.formatterHint === 'currency') {
      result.formatters = [currencyFormatter];
    } else if (field.formatterHint === 'classification') {
      result.formatters = [
        {
          functionId: FORMATTER_CLASSIFICATION_FUNCTION_ID,
          name: field.name,
          description: field.promptHint ?? field.name,
          config: {
            labels: field.enumeration?.map((val: string) => ({ value: val, description: val })) ?? [],
          },
        },
      ];
    }

    result.validators = [highConfidenceValidator];
  }

  return result as FieldState;
};

type RawSuggestion = {
  suggestion: string;
  type: 'raw';
};

type CreateFieldConfigResult = {
  fieldConfig: FieldConfig;
  modelName: string;
};

const findLargestJSON = (text: string): string | null => {
  const matches = text.match(/(\{[^]+\})/g);
  if (!matches) {
    return null;
  }

  return matches.reduce((largest, current) => (current.length > largest.length ? current : largest));
};

export const useModelSuggestion = () => {
  const { client } = useClient();

  const createFieldConfig = async (documentIds: Document[], description: string): Promise<CreateFieldConfigResult> => {
    // We make at most three honest attempts to get a readable field configuration
    try {
      const response: RawSuggestion = await client.makePostRequest('/suggestions', {
        documentId: documentIds[0].documentId,
        text: createDocumentPrompt(description),
        type: 'raw',
      });

      const json = findLargestJSON(response['suggestion']);
      if (json) {
        const result = JSON.parse(json);
        const modelName = result['documentType'] ?? DEFAULT_TEMPLATE;
        const fields = result['fields'].map(applyFormattersAndValidators);

        return { fieldConfig: convertToFieldConfig(fields ?? []), modelName };
      }
    } catch (err) {
      console.error(err);
    }

    return { fieldConfig: {}, modelName: DEFAULT_TEMPLATE };
  };

  return { createFieldConfig };
};
